import { useMemo } from "react";
import { TaskStatus } from "tm-shared/types";

interface TaskPayload {
    status: TaskStatus;
    comment?: string;
}

interface UseTaskCompletionParams<T extends TaskPayload> {
    payloadMap: Record<string, T> | undefined;
    expectedCount: number;
    isPayloadComplete?: (payload: T) => boolean;
}

export const useTaskCompletion = <T extends TaskPayload>({
    payloadMap,
    expectedCount,
    isPayloadComplete,
}: UseTaskCompletionParams<T>) => {
    const isAllCompleted = useMemo(() => {
        if (!payloadMap) return false;

        const payloads = Object.values(payloadMap);

        if (expectedCount !== payloads.length) return false;

        for (const payload of payloads) {
            if (payload.status === TaskStatus.Incomplete) return false;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                return false;
            }

            if (isPayloadComplete && !isPayloadComplete(payload)) {
                return false;
            }
        }

        return true;
    }, [payloadMap, expectedCount, isPayloadComplete]);

    return { isAllCompleted };
};
